import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EmailDesigner from "@/components/EmailDesigner";
import { stripLinkUnderline } from "@/lib/htmlTransforms";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus, Trash, FloppyDisk, PencilSimple, Sparkle,
  DownloadSimple, UploadSimple, ArrowsClockwise, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const VARIABLES = [
  { token: "{{recipient_name}}",    label: "Recipient name" },
  { token: "{{customer_number}}",   label: "Customer #" },
  { token: "{{company_name}}",      label: "Company name" },
  { token: "{{verification_link}}", label: "Verification link (URL)" },
  { token: "{{verify_button}}",     label: "Verify-email button (HTML)" },
];

const EMPTY_T = { id: null, name: "", subject: "", body_html: "", design_json: null };

export function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_T);
  const [saving, setSaving] = useState(false);
  const designerHandleRef = useRef(null);

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryCategory, setLibraryCategory] = useState("All");
  const [installing, setInstalling] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: "", subject: "", file: null });
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data);
    } catch {
      toast.error("Failed to load templates");
    }
  };
  useEffect(() => { load(); }, []);

  const loadLibrary = async () => {
    try {
      const { data } = await api.get("/email-templates/library");
      setLibrary(data.items || []);
    } catch {
      toast.error("Failed to load template library");
    }
  };

  const installFromLibrary = async (lib) => {
    setInstalling(lib.library_id);
    try {
      const { data } = await api.post("/email-templates/library/install", { library_id: lib.library_id });
      toast.success(`Installed "${data.name}" — open it to customise`);
      setLibraryOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const installAllFromLibrary = async () => {
    setInstalling("__all__");
    try {
      const { data } = await api.post("/email-templates/library/install-all", {});
      const inst = data.installed?.length || 0;
      const skip = data.skipped?.length || 0;
      if (inst > 0) {
        toast.success(`Installed ${inst} starter template${inst === 1 ? "" : "s"}${skip > 0 ? ` · ${skip} skipped (already installed)` : ""}`);
      } else {
        toast.info(skip > 0 ? "All library templates are already installed" : "No templates installed");
      }
      setLibraryOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk install failed");
    } finally {
      setInstalling(null);
    }
  };

  const persist = async ({ design_json, html }) => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast.error("Name and subject are required");
      return;
    }
    setSaving(true);
    try {
      const finalHtml = form.no_link_underline ? stripLinkUnderline(html) : html;
      const payload = { name: form.name, subject: form.subject, body_html: finalHtml, design_json };
      if (form.id) {
        await api.patch(`/email-templates/${form.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/email-templates", payload);
        toast.success("Template created");
      }
      setOpen(false);
      setForm(EMPTY_T);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/email-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sendTest = async (t) => {
    const to = window.prompt(`Send a test email of "${t.name}" to:`, "");
    if (to === null) return;
    const target = (to || "").trim();
    if (target && !target.includes("@")) { toast.error("Enter a valid email address"); return; }
    try {
      const { data } = await api.post(`/email-templates/${t.id}/send-test`, target ? { to: target } : {});
      toast.success(`Test sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test send failed");
    }
  };

  const rewrapTemplate = async (t) => {
    if (!window.confirm(`Refresh the wrapper for "${t.name}"?\n\nThis re-applies the latest sizing + padding fixes to imported templates. It won't touch native Unlayer designs.`)) return;
    try {
      await api.post(`/email-templates/${t.id}/rewrap-html-block`);
      toast.success(`Wrapper refreshed for "${t.name}"`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't refresh wrapper — this template may already be a native Unlayer design.");
    }
  };

  const handleUploadSubmit = async (e) => {
    e?.preventDefault?.();
    if (!uploadForm.file) { toast.error("Choose a .zip or .json file to upload"); return; }
    if (!uploadForm.name.trim()) { toast.error("Template name is required"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("name", uploadForm.name.trim());
      fd.append("subject", uploadForm.subject.trim() || uploadForm.name.trim());
      const { data } = await api.post("/email-templates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const mediaCount = (data.attached_media || []).length;
      toast.success(mediaCount > 0 ? `Imported "${data.name}" · ${mediaCount} image${mediaCount === 1 ? "" : "s"} uploaded` : `Imported "${data.name}"`);
      setUploadOpen(false);
      setUploadForm({ name: "", subject: "", file: null });
      if (uploadInputRef.current) uploadInputRef.current.value = "";
      await load();
      setForm({ id: data.id, name: data.name, subject: data.subject, body_html: data.body_html || "", design_json: data.design_json || null });
      setOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed — make sure the ZIP contains index.html + images/ (or design.json)");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="templates-tab">
      <div className="flex flex-wrap gap-2 justify-end mb-3">
        <Button variant="outline" onClick={() => { setLibraryOpen(true); if (library.length === 0) loadLibrary(); }} data-testid="browse-library-btn">
          <Sparkle size={15} className="mr-1.5" weight="bold" /> Browse template library
        </Button>
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="upload-template-btn">
          <UploadSimple size={15} className="mr-1.5" weight="bold" /> Upload from Unlayer
        </Button>
        <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" onClick={() => { setForm(EMPTY_T); setOpen(true); }} data-testid="new-template-btn">
          <Plus size={15} className="mr-1.5" weight="bold" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.length === 0 ? (
          <div className="col-span-full bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-md p-10 text-center" data-testid="templates-empty-state">
            <Sparkle size={36} className="mx-auto mb-3 text-[#002BF6]" weight="duotone" />
            <div className="text-lg font-semibold text-zinc-900 mb-1" style={{ fontFamily: "Work Sans" }}>Your library is ready to seed</div>
            <p className="text-sm text-zinc-600 max-w-md mx-auto mb-5">
              Install all 9 professional + seasonal starter templates in one click. You can fully customise text, images, and design afterwards.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={installAllFromLibrary} disabled={installing === "__all__"} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="seed-all-empty-btn">
                <Sparkle size={14} className="mr-1.5" weight="bold" />
                {installing === "__all__" ? "Installing…" : "Seed all 9 starter templates"}
              </Button>
              <Button variant="outline" onClick={() => { setLibraryOpen(true); if (library.length === 0) loadLibrary(); }} data-testid="browse-library-empty-btn">
                Browse one-by-one
              </Button>
            </div>
          </div>
        ) : (
          templates.map((t) => (
            <div key={t.id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-zinc-900 truncate">{t.name}</div>
                  <div className="text-xs text-zinc-500 truncate mt-0.5">{t.subject}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => sendTest(t)} title="Send a test email to yourself" className="text-zinc-600 hover:text-[#002BF6]" data-testid={`send-test-${t.id}`}>
                    <PaperPlaneTilt size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setForm({ ...EMPTY_T, ...t }); setOpen(true); }} data-testid={`edit-template-${t.id}`}>
                    <PencilSimple size={13} />
                  </Button>
                  {Array.isArray(t.attached_media) && t.attached_media.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => rewrapTemplate(t)} title="Refresh wrapper" className="text-zinc-600 hover:text-[#002BF6]" data-testid={`rewrap-template-${t.id}`}>
                      <ArrowsClockwise size={13} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-red-600 hover:text-red-700" data-testid={`delete-template-${t.id}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              </div>
              <div className="mt-3 text-xs text-zinc-600 line-clamp-4 prose-sm" dangerouslySetInnerHTML={{ __html: t.body_html }} />
            </div>
          ))
        )}
      </div>

      {/* ── Edit / Create dialog ────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-[95vw] max-w-[1200px] max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden p-0 gap-0" data-testid="template-dialog">
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-200 shrink-0">
            <DialogTitle>{form.id ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          <div className="px-5 sm:px-6 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 shrink-0 border-b border-zinc-100 bg-zinc-50">
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Template name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white" data-testid="template-name" placeholder="e.g. Shipment arrived notification" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Email subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 bg-white" data-testid="template-subject" placeholder="e.g. Your package has arrived" />
            </div>
            <label className="sm:col-span-2 flex items-start gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={!!form.no_link_underline} onChange={(e) => setForm({ ...form, no_link_underline: e.target.checked })} data-testid="template-no-link-underline" className="mt-0.5 accent-[#002BF6]" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-zinc-800">Remove underlines from links in this template</div>
                <div className="text-[10px] text-zinc-500 leading-snug mt-0.5">
                  Applied to every <code className="font-mono">&lt;a&gt;</code> tag when the template is saved. Designer keeps the default underline so links remain visible while editing.
                </div>
              </div>
            </label>
          </div>
          <div className="flex-1 overflow-hidden min-h-0">
            {open && (
              <EmailDesigner
                key={form.id || "new"}
                initialDesign={form.design_json || null}
                onChange={(handle) => { designerHandleRef.current = handle; }}
                onSave={(data) => persist(data)}
                height={520}
              />
            )}
          </div>
          <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 bg-white flex items-center justify-between shrink-0">
            <div className="text-[11px] text-zinc-500">
              Merge tags: {VARIABLES.map(v => v.token).join(" · ")} — insert from the Merge Tags panel inside the designer.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="template-cancel-btn">Cancel</Button>
              <Button
                onClick={async () => {
                  const handle = designerHandleRef.current;
                  if (!handle?.exportHtml) { toast.error("Designer not ready"); return; }
                  const exported = await handle.exportHtml();
                  if (!exported) return;
                  await persist({ design_json: exported.design, html: exported.html });
                }}
                disabled={saving}
                className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
                data-testid="save-template-btn"
              >
                <FloppyDisk size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save template"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Upload dialog ───────────────────────────────────────────── */}
      <Dialog open={uploadOpen} onOpenChange={(v) => { setUploadOpen(v); if (!v) { setUploadForm({ name: "", subject: "", file: null }); if (uploadInputRef.current) uploadInputRef.current.value = ""; } }}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-[520px] p-0 gap-0 overflow-hidden" data-testid="upload-template-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-200">
            <DialogTitle className="flex items-center gap-2">
              <UploadSimple size={18} className="text-[#002BF6]" weight="bold" />
              Import Unlayer template
            </DialogTitle>
            <div className="text-xs text-zinc-500 mt-1 leading-snug">
              Upload a <b>.zip</b> exported from Unlayer (containing <code>index.html</code> + <code>images/</code>), or a standalone Unlayer <b>design.json</b>. Images are hosted publicly and every reference is rewritten automatically.
            </div>
          </DialogHeader>
          <form onSubmit={handleUploadSubmit} className="px-5 py-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">Template name</label>
              <input type="text" value={uploadForm.name} onChange={(e) => setUploadForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Welcome — KYo unverified customers" className="w-full px-3 py-2 text-sm border border-zinc-300 rounded" required data-testid="upload-template-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">Email subject <span className="text-zinc-400 font-normal">(optional)</span></label>
              <input type="text" value={uploadForm.subject} onChange={(e) => setUploadForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Defaults to the template name" className="w-full px-3 py-2 text-sm border border-zinc-300 rounded" data-testid="upload-template-subject" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">Unlayer export (.zip or .json)</label>
              <input ref={uploadInputRef} type="file" accept=".zip,application/zip,application/x-zip-compressed,.json,application/json" onChange={(e) => setUploadForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} className="w-full text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-[#002BF6] file:text-white hover:file:bg-[#0021C7] file:cursor-pointer cursor-pointer" required data-testid="upload-template-file" />
              {uploadForm.file && <div className="mt-1.5 text-[11px] text-zinc-500">{uploadForm.file.name} · {(uploadForm.file.size / 1024).toFixed(0)} KB</div>}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading} data-testid="upload-template-cancel">Cancel</Button>
              <Button type="submit" className="bg-[#002BF6] hover:bg-[#0021C7] text-white" disabled={uploading || !uploadForm.file || !uploadForm.name.trim()} data-testid="upload-template-submit">
                <UploadSimple size={14} className="mr-1.5" weight="bold" />
                {uploading ? "Uploading…" : "Import template"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Library picker dialog ────────────────────────────────────── */}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-[95vw] max-w-[900px] max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden p-0 gap-0" data-testid="library-dialog">
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-200 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkle size={18} className="text-[#002BF6]" weight="bold" />
              Template library
            </DialogTitle>
            <div className="text-xs text-zinc-500 mt-1">Pick a professional or seasonal template to install. You can edit text, swap images, and tweak the design afterwards.</div>
          </DialogHeader>
          <div className="px-5 sm:px-6 py-2 border-b border-zinc-100 flex flex-wrap gap-1.5 shrink-0">
            {["All", "Operational", "Seasonal", "Marketing"].map((c) => (
              <button key={c} onClick={() => setLibraryCategory(c)} className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${libraryCategory === c ? "bg-[#002BF6] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`library-cat-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
            {library.length === 0 ? (
              <div className="text-center text-sm text-zinc-500 py-10">Loading library…</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {library.filter((l) => libraryCategory === "All" || l.category === libraryCategory).map((l) => (
                  <div key={l.library_id} className="border border-zinc-200 rounded-md p-4 hover:border-[#002BF6] hover:shadow-sm transition-all bg-white" data-testid={`library-card-${l.library_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="font-semibold text-zinc-900 text-sm">{l.name}</div>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${l.category === "Seasonal" ? "bg-amber-100 text-amber-800" : l.category === "Marketing" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}`}>{l.category}</span>
                    </div>
                    <div className="text-xs text-zinc-600 line-clamp-2 mb-2"><b className="text-zinc-700">Subject:</b> {l.subject}</div>
                    <div className="text-[11px] text-zinc-500 italic mb-3 line-clamp-2">{l.preview_text}</div>
                    <Button size="sm" onClick={() => installFromLibrary(l)} disabled={installing === l.library_id} className="w-full bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid={`install-library-${l.library_id}`}>
                      <DownloadSimple size={13} className="mr-1.5" weight="bold" />
                      {installing === l.library_id ? "Installing…" : "Install"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 bg-zinc-50 flex flex-wrap items-center justify-between gap-2 shrink-0">
            <Button onClick={installAllFromLibrary} disabled={installing === "__all__"} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="install-all-library-btn">
              <Sparkle size={13} className="mr-1.5" weight="bold" />
              {installing === "__all__" ? "Installing all…" : "Install all 9 templates"}
            </Button>
            <Button variant="outline" onClick={() => setLibraryOpen(false)} data-testid="library-close-btn">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
